<template>
  <div class="bid-history-list">
    <div class="bid-history-list__head">
      <span class="bid-history-list__title">Bid history</span>
      <span class="bid-history-list__count">{{ rows.length }}</span>
    </div>

    <div class="bid-history-list__body">
      <div v-if="rows.length === 0" class="bid-history-list__empty">No activity on this asset yet.</div>

      <template v-for="(entry, index) in entries" :key="entry.row.id ?? index">
        <!-- A bid, accepted or rejected: who bid, from where, how much. -->
        <div
          v-if="entry.isBid"
          class="bid-history-list__row"
          :class="{
            'bid-history-list__row--rejected': entry.row.rejected,
            'bid-history-list__row--newest': index === 0 && !entry.row.rejected,
            'bid-history-list__row--last': index === entries.length - 1,
          }"
        >
          <SourceBadge :source="entry.row.source" :size="22" />
          <span class="bid-history-list__detail">
            <span class="bid-history-list__name">
              {{ entry.row.bidder }}
              <span v-if="entry.row.rejected" class="bid-history-list__rejected-tag">rejected</span>
            </span>
            <span class="bid-history-list__meta" :title="entry.row.reason || undefined">{{ entry.meta }}</span>
          </span>
          <span
            v-if="entry.row.amount != null"
            class="bid-history-list__amount"
            :class="[
              `bid-history-list__amount--${entry.row.source}`,
              { 'bid-history-list__amount--rejected': entry.row.rejected },
            ]"
          >${{ entry.row.amount.toLocaleString() }}</span>
        </div>

        <!-- Everything the clerk or the engine did to the lot. -->
        <div
          v-else
          class="bid-history-list__row bid-history-list__row--system"
          :class="{
            'bid-history-list__row--rejected': entry.row.rejected,
            'bid-history-list__row--settled': entry.row.kind === 'finalize',
            'bid-history-list__row--last': index === entries.length - 1,
          }"
        >
          <span
            class="bid-history-list__glyph"
            :class="{ 'bid-history-list__glyph--settled': entry.row.kind === 'finalize' }"
          >{{ entry.glyph }}</span>
          <span class="bid-history-list__detail">
            <span class="bid-history-list__system-label">
              {{ entry.label }}
              <span v-if="entry.qualifier" class="bid-history-list__qualifier">{{ entry.qualifier }}</span>
              <span v-if="entry.row.rejected" class="bid-history-list__rejected-tag">rejected</span>
            </span>
            <span class="bid-history-list__meta" :title="entry.row.reason || undefined">{{ entry.meta }}</span>
          </span>
          <span
            v-if="entry.figure != null"
            class="bid-history-list__figure"
            :class="{ 'bid-history-list__amount--rejected': entry.row.rejected }"
          >{{ entry.figure }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SourceBadge from '../SourceBadge/SourceBadge.vue';

// The lot's ledger, newest first.
//
// It is not a bid list — it is everything that happened to the lot, because
// that is what a clerk needs when a seller disputes an outcome: the bids, the
// increments the clerk changed, the asking prices they dropped, the undos, the
// pauses, the reset, and the settle. A rejected action keeps its place, struck
// through with the engine's reason, rather than vanishing; a row that
// disappears looks like the clerk imagined it.
//
// Eleven row kinds used to be eleven near-identical blocks in the template,
// each repeating the divider logic. They are two now — a bid and a system entry
// — with the glyph and wording of each kind in one table below.
defineOptions({ name: 'BidHistoryList' });

const props = defineProps({
  // Newest first. Every row has { id, kind, t }; the rest depends on the kind.
  // `t` is a pre-formatted clock, because how a time reads is the
  // application's decision.
  rows: { type: Array, default: () => [] },
});

// kind → the glyph in the gutter and how the row reads. A kind that is not here
// falls through to a bid, which is the shape an ordinary history row has and the
// one a caller with no `kind` at all means.
const SYSTEM_ENTRIES = {
  increment: { glyph: '↕', label: 'Increment' },
  asking: { glyph: '$', label: 'Asking' },
  undo: { glyph: '↶', label: 'Undo' },
  floorOverride: { glyph: '⚑', label: 'Floor override' },
  reset: { glyph: '↺', label: 'Bidding reset' },
  pause: { glyph: '⏸', label: 'Paused' },
  resume: { glyph: '▶', label: 'Resumed' },
  proxyDelete: { glyph: '✕', label: 'Proxies cleared' },
  finalize: { glyph: '✓', label: null },
};

// What sits on the right of a system row: a price, an increment, a count, or
// nothing. A rejected action shows what it TRIED to do, which is the useful
// thing when reading a reason next to it.
function figureFor(row) {
  if (row.kind === 'asking' && row.askingPrice != null) return `$${row.askingPrice.toLocaleString()}`;
  if (row.kind === 'proxyDelete' && row.count != null) return String(row.count);
  if (row.kind === 'finalize' && row.amount != null) return `$${row.amount.toLocaleString()}`;
  if (row.rejected) return null;
  if (row.increment != null) return `$${row.increment.toLocaleString()}`;
  if (row.bid != null) return `$${row.bid.toLocaleString()}`;
  return null;
}

// The second line. A rejection replaces it with the engine's reason, which is
// the only thing worth reading on a row that did not take effect.
function metaFor(row, isBid) {
  if (row.rejected) return row.reason;
  if (row.kind === 'pause') return row.reason || row.t;
  if (isBid) return row.badge ? `${row.badge} · ${row.t}` : row.t;
  if (row.kind === 'finalize') return row.badge ? `${row.badge} · ${row.t}` : row.t;
  return row.t;
}

const entries = computed(() =>
  props.rows.map((row) => {
    const system = SYSTEM_ENTRIES[row.kind];
    const isBid = system === undefined;
    return {
      row,
      isBid,
      glyph: system?.glyph,
      // `finalize` carries its own wording — "Sold", "No Sale", "Offer" — since
      // the outcome IS the label.
      label: row.kind === 'finalize' ? row.label : system?.label,
      // Names what an undo reversed, e.g. "increment".
      qualifier: row.kind === 'undo' ? row.what : null,
      figure: isBid ? null : figureFor(row),
      meta: metaFor(row, isBid),
    };
  })
);
</script>

<style scoped>
.bid-history-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--p-bid-history-list-background);
  border: var(--p-bid-history-list-border-width) solid var(--p-bid-history-list-border-color);
  border-radius: var(--p-bid-history-list-border-radius);
}

.bid-history-list__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--p-bid-history-list-head-padding);
  border-bottom: var(--p-bid-history-list-head-border-bottom-width) solid
    var(--p-bid-history-list-head-border-bottom-color);
  background: var(--p-bid-history-list-head-background);
}

.bid-history-list__title {
  text-transform: uppercase;
  font-size: var(--p-bid-history-list-title-font-size);
  font-weight: var(--p-bid-history-list-title-font-weight);
  letter-spacing: var(--p-bid-history-list-title-letter-spacing);
  color: var(--p-bid-history-list-title-color);
}

.bid-history-list__count {
  font-family: var(--p-bid-history-list-count-font-family);
  font-size: var(--p-bid-history-list-count-font-size);
  color: var(--p-bid-history-list-count-color);
}

.bid-history-list__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.bid-history-list__empty {
  font-style: italic;
  padding: var(--p-bid-history-list-empty-padding);
  font-size: var(--p-bid-history-list-empty-font-size);
  color: var(--p-bid-history-list-empty-color);
}

.bid-history-list__row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--p-bid-history-list-row-gap);
  padding: var(--p-bid-history-list-row-padding);
  border-bottom: var(--p-bid-history-list-row-border-bottom-width) solid
    var(--p-bid-history-list-row-border-bottom-color);
}

.bid-history-list__row--last {
  border-bottom-width: 0;
}

/* The standing high bid, washed so the eye lands on it first. */
.bid-history-list__row--newest {
  background: var(--p-bid-history-list-row-newest-background);
}

/* A clerk or engine action, set back from the bids around it. */
.bid-history-list__row--system {
  background: var(--p-bid-history-list-row-system-background);
}

/* The settle is the row the whole list exists to justify. */
.bid-history-list__row--settled {
  background: var(--p-bid-history-list-row-settled-background);
}

.bid-history-list__row--rejected {
  opacity: 0.72;
}

.bid-history-list__detail {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.bid-history-list__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-bid-history-list-name-font-size);
  font-weight: var(--p-bid-history-list-name-font-weight);
  color: var(--p-bid-history-list-name-color);
}

.bid-history-list__meta {
  font-family: var(--p-bid-history-list-meta-font-family);
  font-size: var(--p-bid-history-list-meta-font-size);
  color: var(--p-bid-history-list-meta-color);
}

.bid-history-list__amount {
  font-family: var(--p-bid-history-list-amount-font-family);
  font-size: var(--p-bid-history-list-amount-font-size);
  font-weight: var(--p-bid-history-list-amount-font-weight);
  font-variant-numeric: tabular-nums;
}

/* An online bid and a floor bid are told apart by color as well as by the
   badge, because the history is read at a glance from across the block. */
.bid-history-list__amount--online {
  color: var(--p-bid-history-list-amount-online-color);
}

.bid-history-list__amount--floor,
.bid-history-list__amount--prebid {
  color: var(--p-bid-history-list-amount-color);
}

.bid-history-list__amount--rejected {
  text-decoration: line-through;
  color: var(--p-bid-history-list-amount-rejected-color);
  font-weight: var(--p-bid-history-list-amount-rejected-font-weight);
}

.bid-history-list__glyph {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-bid-history-list-glyph-border-radius);
  background: var(--p-bid-history-list-glyph-background);
  border: var(--p-bid-history-list-glyph-border-width) solid var(--p-bid-history-list-glyph-border-color);
  color: var(--p-bid-history-list-glyph-color);
  font-size: var(--p-bid-history-list-glyph-font-size);
}

.bid-history-list__glyph--settled {
  color: var(--p-bid-history-list-glyph-settled-color);
  border-color: var(--p-bid-history-list-glyph-settled-border-color);
}

.bid-history-list__system-label {
  text-transform: uppercase;
  font-size: var(--p-bid-history-list-system-label-font-size);
  font-weight: var(--p-bid-history-list-system-label-font-weight);
  letter-spacing: var(--p-bid-history-list-system-label-letter-spacing);
  color: var(--p-bid-history-list-system-label-color);
}

.bid-history-list__qualifier {
  text-transform: uppercase;
  font-size: var(--p-bid-history-list-qualifier-font-size);
  font-weight: var(--p-bid-history-list-qualifier-font-weight);
  letter-spacing: var(--p-bid-history-list-qualifier-letter-spacing);
  color: var(--p-bid-history-list-qualifier-color);
}

.bid-history-list__rejected-tag {
  text-transform: uppercase;
  font-size: var(--p-bid-history-list-rejected-tag-font-size);
  font-weight: var(--p-bid-history-list-rejected-tag-font-weight);
  letter-spacing: var(--p-bid-history-list-rejected-tag-letter-spacing);
  color: var(--p-bid-history-list-rejected-tag-color);
}

.bid-history-list__figure {
  font-family: var(--p-bid-history-list-figure-font-family);
  font-size: var(--p-bid-history-list-figure-font-size);
  font-weight: var(--p-bid-history-list-figure-font-weight);
  color: var(--p-bid-history-list-figure-color);
  font-variant-numeric: tabular-nums;
}
</style>
